import { type DocBlock } from './_Doc.ts'
import { type EMRKindID, type EMRTypeID, type EncounterStatus } from './_EMR.ts'
import { HEADING_1, NORMAL_TEXT, ParseError, TITLE, TokenStream } from './_parsing.ts'
import { docifyRecommendationContent, parseRecommendationContent, type RecommendationContent } from './Recommendation.ts'
import { type ServiceID } from './ServiceProduct.ts'

export interface ReportContent {
	type: EMRTypeID
	preliminaryNotes?: string
	notes: string
	messageForAssistant?: string
	recommendation?: RecommendationContent
}

export const Migrated_ID = 'imago.migrated' as EMRTypeID & EMRKindID

export const WelcomeCall_ID = 'imago.welcome-call' as ServiceID

export const Welcome_ID = 'imago.welcome-call' as EMRTypeID
const Welcome_Scheduled_ID = Welcome_ID + ':scheduled' as EMRKindID
const Welcome_Cancelled_ID = Welcome_ID + ':cancelled' as EMRKindID
const Welcome_NoShow_ID = Welcome_ID + ':no-show' as EMRKindID
const Welcome_WithWelcomed_ID = 'imago.welcomed' as EMRKindID

export const GAHTTransferCall_ID = 'imago.gaht.transfer-call' as ServiceID
export const GAHTSetupCall_ID = 'imago.gaht.setup-call' as ServiceID

export const GAHTSetup_ID = 'imago.gaht.setup-call' as EMRTypeID
const GAHTSetup_Scheduled_ID = GAHTSetup_ID + ':scheduled' as EMRKindID
const GAHTSetup_Cancelled_ID = GAHTSetup_ID + ':cancelled' as EMRKindID
const GAHTSetup_Ready_ID = GAHTSetup_ID + ':ready' as EMRKindID
const GAHTSetup_NoShow_ID = GAHTSetup_ID + ':no-show' as EMRKindID
const GAHTSetup_Done_ID = GAHTSetup_ID + '' as EMRKindID
const GAHTSetup_Closed_WithRecommendation_ID = 'imago.gaht.prescription-draft' as EMRKindID

export const GAHTReview_ID = 'imago.gaht.treatment-review' as EMRTypeID
const GAHTReview_Scheduled_ID = GAHTReview_ID + ':scheduled' as EMRKindID
const GAHTReview_Ready_ID = GAHTReview_ID + ':ready' as EMRKindID
const GAHTReview_WithRecommendation_ID = GAHTReview_ID + ':with-prescription-draft' as EMRKindID
const GAHTReview_Closed_WithRecommendation_ID = GAHTReview_ID + ':closed:with-prescription-draft' as EMRKindID

const REPORT_STATUSES = new Map<EMRKindID, EncounterStatus>([
	[Welcome_Scheduled_ID, 'scheduled'],
	[Welcome_Cancelled_ID, 'cancelled'],
	[Welcome_NoShow_ID, 'no-show'],
	[Welcome_WithWelcomed_ID, 'done'],
	[GAHTSetup_Scheduled_ID, 'scheduled'],
	[GAHTSetup_Cancelled_ID, 'cancelled'],
	[GAHTSetup_Ready_ID, 'ready'],
	[GAHTSetup_NoShow_ID, 'no-show'],
	[GAHTSetup_Done_ID, 'done'],
	[GAHTSetup_Closed_WithRecommendation_ID, 'done'],
	[GAHTReview_Scheduled_ID, 'scheduled'],
	[GAHTReview_Ready_ID, 'ready'],
	[GAHTReview_WithRecommendation_ID, 'done'],
	[GAHTReview_Closed_WithRecommendation_ID, 'closed'],
])

const REPORT_TYPES = new Map<EMRKindID, EMRTypeID>([
	[Welcome_Scheduled_ID, Welcome_ID],
	[Welcome_Cancelled_ID, Welcome_ID],
	[Welcome_NoShow_ID, Welcome_ID],
	[Welcome_WithWelcomed_ID, Welcome_ID],
	[GAHTSetup_Scheduled_ID, GAHTSetup_ID],
	[GAHTSetup_Cancelled_ID, GAHTSetup_ID],
	[GAHTSetup_Ready_ID, GAHTSetup_ID],
	[GAHTSetup_NoShow_ID, GAHTSetup_ID],
	[GAHTSetup_Done_ID, GAHTSetup_ID],
	[GAHTSetup_Closed_WithRecommendation_ID, GAHTSetup_ID],
	[GAHTReview_Scheduled_ID, GAHTReview_ID],
	[GAHTReview_Ready_ID, GAHTReview_ID],
	[GAHTReview_WithRecommendation_ID, GAHTReview_ID],
	[GAHTReview_Closed_WithRecommendation_ID, GAHTReview_ID],
])

export const REPORT_KINDS = {
	[Welcome_ID]: {
		scheduled: Welcome_Scheduled_ID,
		noshow: Welcome_NoShow_ID,
		cancelled: Welcome_Cancelled_ID,
		done: Welcome_WithWelcomed_ID,
	},
	[GAHTSetup_ID]: {
		scheduled: GAHTSetup_Scheduled_ID,
		ready: GAHTSetup_Ready_ID,
		noshow: GAHTSetup_NoShow_ID,
		cancelled: GAHTSetup_Cancelled_ID,
		done: GAHTSetup_Closed_WithRecommendation_ID,
	},
	[GAHTReview_ID]: {
		scheduled: GAHTReview_Scheduled_ID,
		ready: GAHTReview_Ready_ID,
		done: GAHTReview_WithRecommendation_ID,
		closed: GAHTReview_Closed_WithRecommendation_ID,
	},
}

export const REPORT_TITLES = new Map<EMRTypeID, string>([
	[Welcome_ID, "Welcome Call Report"],
	[GAHTSetup_ID, "GAHT Setup Call Report"],
	[GAHTReview_ID, "GAHT Treatment Review Report"],
])

export function parseKind(kindID: EMRKindID) {
	return {
		type: REPORT_TYPES.get(kindID) ?? (kindID as unknown as EMRTypeID),
		callStatus: REPORT_STATUSES.get(kindID),
		hasWelcomed: kindID === Welcome_WithWelcomed_ID,
		hasRecommendation: kindID === GAHTSetup_Closed_WithRecommendation_ID || kindID === GAHTReview_WithRecommendation_ID || kindID === GAHTReview_Closed_WithRecommendation_ID,
	}
}

// Staff hand-create these to mark bookable slots; YCBM reads them to offer times. They're not encounters.
const AVAILABILITY_TITLE = 'available'

// True for calendar events that are not encounters. Note that Calendar omits `summary` entirely for an
// untitled event rather than sending '', and an untitled event is in practice a mistyped availability block.
export function isNotAnEncounter(ce: {
	summary?: string | null
}) {
	return !ce.summary || ce.summary.trim().toLowerCase() === AVAILABILITY_TITLE
}

export function determineCallInfo(ce: {
	summary?: string | null // TODO strongly type after Google Workspace lib strongly types it.
}) {
	const summary = ce.summary
	if (!summary) {
		// Never classify an untitled event as a call - it must not reach the default below.
		return {
			participantName: undefined,
			topic: '',
			type: undefined,
		}
	}

	let participantName, topic
	let splitSummary = summary.split(' / ')
	if (!splitSummary[1]) {
		splitSummary = summary.split(' and ')
	}
	if (splitSummary[1]) {
		participantName = splitSummary[0]
		topic = splitSummary[1].replace('Imago ', '')
	} else {
		topic = summary
	}

	if (topic.startsWith('TEST ')) {
		topic = topic.substring('TEST '.length)
	}

	if (topic === 'doctor') { // TODO remove
		topic = 'GAHT setup call'
	}
	if (topic === 'introductory call') { // TODO remove
		topic = 'welcome call'
	}

	const lowertopic = topic.toLowerCase()
	const service: ServiceID = lowertopic === 'welcome call' ? WelcomeCall_ID : lowertopic === 'gaht setup call' ? GAHTSetupCall_ID : lowertopic === 'gaht transfer call' ? GAHTTransferCall_ID : lowertopic === 'call with zofia' ? WelcomeCall_ID : GAHTSetupCall_ID // TODO we default to GAHT Setup Call, for now

	const type: EMRTypeID | undefined =
		(service === WelcomeCall_ID) ? Welcome_ID :
		(service === GAHTSetupCall_ID) ? GAHTSetup_ID :
		(service === GAHTTransferCall_ID) ? GAHTSetup_ID :
		undefined

	return {
		participantName,
		topic,
		type,
	}
}

const docifyType = REPORT_TITLES
const parseType = new Map([...docifyType.entries()].map(([t, s]) => [s, t]))

export function docifyReport(report: ReportContent): DocBlock[] {
	const title = docifyType.get(report.type)
	if (!title)
		throw new TypeError('Invalid report')
	return [
		{ class: TITLE, text: title },
		{ class: NORMAL_TEXT, text: report.notes },
		...(report.messageForAssistant ? [
			{ class: HEADING_1, text: "Message for an assistant" },
			{ class: NORMAL_TEXT, text: report.messageForAssistant },
		] : []),
		...(report.recommendation ? [
			{ class: HEADING_1, text: "Recommendation" },
			...docifyRecommendationContent(report.recommendation)
		] : []),
		...(report.preliminaryNotes ? [
			{ class: HEADING_1, text: "Preliminary notes" },
			{ class: NORMAL_TEXT, text: report.preliminaryNotes },
		] : []),
	]
}

export function parseReport(md: DocBlock[]): ReportContent {
	const ts = new TokenStream(md[Symbol.iterator]())

	const title = ts.matchClass(TITLE)
	const type = parseType.get(title)
	if (!type) {
		throw new ParseError()
	}
	const notes = ts.consumePlainContent()

	const report: ReportContent = { type, notes }

	while (true) {
		if (ts.tryMatch(HEADING_1, "Prescription draft") || ts.tryMatch(HEADING_1, "Recommendation")) {
			report.recommendation = parseRecommendationContent(ts)
			continue
		}

		if (ts.tryMatch(HEADING_1, "Message for an assistant")) {
			report.messageForAssistant = ts.consumePlainContent()
			continue
		}

		if (ts.tryMatch(HEADING_1, "Preliminary notes")) {
			report.preliminaryNotes = ts.consumePlainContent()
			continue
		}

		break
	}

	return report
}
